import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Badge } from '../../../../../../../components/ui/badge';

interface PipeParametersTableProps {
    jobData: any;
    pipeParamKey?: string;
}

const PipeParametersTable: React.FC<PipeParametersTableProps> = ({ jobData, pipeParamKey }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    
    // Use provided key or find the first PIPE PARAMETERS key
    const parsedData = jobData?.parsed_json_data || {};
    const pipeParamsKey = pipeParamKey || Object.keys(parsedData).find(key => key.startsWith('PIPE PARAMETERS'));
    const pipeParams = pipeParamsKey ? parsedData[pipeParamsKey] : null;

    // Force scroll recalculation when pipeParamKey changes
    useEffect(() => {
        if (scrollContainerRef.current && pipeParamsKey) {
            // Small delay to ensure DOM is fully updated with new data
            const timer = setTimeout(() => {
                const container = scrollContainerRef.current;
                if (container) {
                    // Force browser to recalculate layout and scroll dimensions
                    // This ensures scrollbars appear when content overflows
                    const currentScrollLeft = container.scrollLeft;
                    const currentScrollTop = container.scrollTop;
                    
                    // Trigger a reflow by reading layout properties
                    void container.offsetWidth;
                    void container.offsetHeight;
                    void container.scrollWidth;
                    void container.scrollHeight;
                    
                    // Restore scroll position if needed
                    container.scrollLeft = currentScrollLeft;
                    container.scrollTop = currentScrollTop;
                }
            }, 150);
            
            return () => clearTimeout(timer);
        }
    }, [pipeParamsKey]);

   //    console.log('M500 PipeParametersTable - pipeParams:', pipeParams);

    if (!pipeParams) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p>No pipe parameters available</p>
            </div>
        );
    }

    const headers = pipeParams.headers || {};
    const values = pipeParams.values || {};

    //console.log('M500 PipeParametersTable - headers:', headers);
   // console.log('M500 PipeParametersTable - values:', values);

    // Extract pass groups (DA, DB, DC, DD, etc.)
    const passGroups = Object.keys(headers);

    // Get all parameters from different categories
    const allParameters: any[] = [];
    const categories = Object.keys(values);

    categories.forEach(category => {
        const categoryData = values[category];
      //  console.log(`M500 Category "${category}":`, categoryData);
        Object.keys(categoryData).forEach(paramKey => {
            const param = categoryData[paramKey];
          //  console.log(`M500 Param "${paramKey}":`, param);
            if (param.value && Array.isArray(param.value)) {
                allParameters.push({
                    category,
                    key: paramKey,
                    text: param.text || paramKey,
                    values: param.value,
                    units: param.units
                });
            }
        });
    });

   // console.log('M500 allParameters:', allParameters);

    // Group parameters by category
    const paramsByCategory: { [key: string]: any[] } = {};
    allParameters.forEach(param => {
        if (!paramsByCategory[param.category]) {
            paramsByCategory[param.category] = [];
        }
        paramsByCategory[param.category].push(param);
    });

    const getStatusBadge = (value: string) => {
        if (value === 'Enabled' || value === 'Yes') {
            return <Badge className="bg-green-600 hover:bg-green-700 text-white text-xs">Enabled</Badge>;
        } else if (value === 'Disabled' || value === 'No') {
            return <Badge className="bg-red-600 hover:bg-red-700 text-white text-xs">Disabled</Badge>;
        }
        return <span className="font-mono text-sm">{value}</span>;
    };

    const formatValue = (value: string) => {
        if (!value) return '-';
        return value;
    };

    const getUnitLabel = (units: any) => {
        if (!units || typeof units !== 'object') return '';
        const unit = units.imperial || units.unit || '';
        return unit ? ` (${unit})` : '';
    };

    return (
        <>
            <style>{`
                .m500-pipe-scroll-container::-webkit-scrollbar {
                    width: 12px;
                    height: 12px;
                }
                .m500-pipe-scroll-container::-webkit-scrollbar-track {
                    background: #f3f4f6;
                    border-radius: 6px;
                }
                .m500-pipe-scroll-container::-webkit-scrollbar-thumb {
                    background: #9ca3af;
                    border-radius: 6px;
                    border: 2px solid #f3f4f6;
                }
                .m500-pipe-scroll-container::-webkit-scrollbar-thumb:hover {
                    background: #6b7280;
                }
                .m500-pipe-scroll-container::-webkit-scrollbar-corner {
                    background: #f3f4f6;
                }
            `}</style>
            <div className="w-full h-full flex flex-col min-h-0">
                <Card className="bg-white border border-gray-200 h-full flex flex-col min-h-0">
                    <CardContent className="p-1 w-full h-full flex flex-col min-h-0">
                        {/* Single scroll container - required for sticky positioning */}
                        <div 
                            ref={scrollContainerRef}
                            className="flex-1 overflow-x-auto overflow-y-auto min-h-0 m500-pipe-scroll-container" 
                            style={{ 
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#9ca3af #f3f4f6',
                                WebkitOverflowScrolling: 'touch',
                                overscrollBehaviorX: 'contain'
                            }}
                        >
                            <table className="w-full caption-bottom text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}>
                            <thead className="sticky top-0 bg-white z-20" style={{ boxShadow: '0 2px 0 0 rgba(209, 213, 219, 1)' }}>
                                {/* First Header Row - Pass Groups */}
                                <tr>
                                    <th 
                                        rowSpan={2}
                                        className="min-w-[200px] max-w-[200px] bg-gray-100 px-3 py-3 text-sm font-bold text-gray-800 text-left sticky left-0 z-30 border-r-2 border-gray-300 border-b-2 border-gray-300 h-12 align-middle"
                                    >
                                        Parameter
                                    </th>
                                    {passGroups.map((passKey) => {
                                        const pass = headers[passKey];
                                        const zones = Object.keys(pass.zones || {});
                                        return (
                                            <th 
                                                key={passKey}
                                                colSpan={zones.length}
                                                className="min-w-[120px] bg-blue-50 px-2 py-2 text-sm font-bold text-blue-700 text-center border-r border-gray-300 border-b border-gray-300 h-12 align-middle"
                                            >
                                                {pass.name}
                                            </th>
                                        );
                                    })}
                                </tr>
                                {/* Second Header Row - Zone Names - This is the last row in thead, so it needs bottom border */}
                                <tr>
                                    {passGroups.map((passKey) => {
                                        const pass = headers[passKey];
                                        const zones = Object.keys(pass.zones || {});
                                        return (
                                            <React.Fragment key={passKey}>
                                                {zones.map((zoneKey) => {
                                                    const zone = pass.zones[zoneKey];
                                                    return (
                                                        <th 
                                                            key={zoneKey}
                                                            className="min-w-[100px] bg-blue-100 px-2 py-2 text-xs font-semibold text-blue-800 text-center border-r border-gray-300 border-b-2 border-gray-300 h-12 align-middle"
                                                        >
                                                            {zone?.name || zoneKey}
                                                        </th>
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="[&_tr:last-child]:border-0">
                                    {Object.entries(paramsByCategory).map(([category, params], categoryIndex) => (
                                        <React.Fragment key={categoryIndex}>
                                            {/* Category Header Row */}
                                            <tr className="bg-gray-200 transition-colors hover:bg-gray-50/50">
                                                {/* First column - Category name, sticky left */}
                                                <td 
                                                    className="px-3 py-2 text-sm font-bold text-gray-800 bg-gray-200 sticky left-0 z-10 border-r-2 border-gray-300 border-b-2 border-gray-300 align-middle"
                                                    style={{ top: '96px' }}
                                                >
                                                    {category}
                                                </td>
                                                {/* Remaining columns - one for each zone */}
                                                {passGroups.map((passKey) => {
                                                    const pass = headers[passKey];
                                                    const zones = Object.keys(pass.zones || {});
                                                    return (
                                                        <React.Fragment key={passKey}>
                                                            {zones.map((zoneKey) => {
                                                                return (
                                                                    <td 
                                                                        key={zoneKey}
                                                                        className="px-2 py-2 text-sm font-bold text-gray-800 bg-gray-200 align-middle"
                                                                    >
                                                                        {/* Empty cell with same styling */}
                                                                    </td>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tr>

                                            {/* Parameter Rows */}
                                            {params.map((param, paramIndex) => {
                                                // Special handling for these 4 specific parameters that should always show merged values
                                                const specialGlobalParams = [
                                                    'Pipe Enable',
                                                    'PQR Number',
                                                    'Pipe Outside Diameter',
                                                    'Welding Power Supply'
                                                ];
                                                
                                                const isSpecialParam = specialGlobalParams.some(specialParam => 
                                                    param.text.includes(specialParam) || param.key.includes(specialParam)
                                                );
                                                
                                                // Check if this parameter has only global values (all cells same)
                                                // Global value can be: groupName === 'D' or only one value in array
                                                // OR if it's one of the special 4 parameters
                                                const hasGlobalValue = isSpecialParam || 
                                                    (param.values.length === 1 && 
                                                     (param.values[0].groupName === 'D' || !param.values[0].groupName));
                                                
                                                // Calculate total columns
                                                const totalColumns = passGroups.reduce((sum, passKey) => {
                                                    return sum + Object.keys(headers[passKey].zones || {}).length;
                                                }, 0);
                                                
                                                return (
                                                    <tr key={paramIndex} className="transition-colors hover:bg-gray-50/50">
                                                        <td className="w-[200px] min-w-[200px] max-w-[200px] bg-white px-3 py-2 text-sm font-medium text-gray-700 sticky left-0 z-10 border-r-2 border-gray-300 border-b border-gray-200 align-middle">
                                                            {param.text}{getUnitLabel(param.units)}
                                                        </td>
                                                        {hasGlobalValue ? (
                                                            // Merge all columns if global value
                                                            <td 
                                                                colSpan={totalColumns}
                                                                className="px-2 py-2 text-sm text-center text-gray-700 bg-white font-mono border-b border-gray-200 align-middle"
                                                            >
                                                                {(() => {
                                                                    // For special params, try to find the value in the array
                                                                    let globalValueObj = null;
                                                                    let displayValue = null;
                                                                    
                                                                    if (isSpecialParam) {
                                                                        // For special params, try to find value with groupName 'D' or first value
                                                                        globalValueObj = param.values.find((v: any) => 
                                                                            v.groupName === 'D' || !v.groupName || v.groupName === ''
                                                                        ) || param.values[0];
                                                                        
                                                                        // Try multiple ways to extract the value
                                                                        displayValue = globalValueObj?.displayName || 
                                                                                       globalValueObj?.value || 
                                                                                       globalValueObj?.text;
                                                                    } else {
                                                                        // Regular global value handling
                                                                        globalValueObj = param.values[0];
                                                                        displayValue = globalValueObj?.displayName || globalValueObj?.value;
                                                                    }
                                                                    
                                                                    // Handle null, undefined, or empty string
                                                                    if (displayValue === undefined || displayValue === null || displayValue === '') {
                                                                        displayValue = '-';
                                                                    } else {
                                                                        displayValue = String(displayValue);
                                                                    }
                                                                    
                                                                    const isStatus = param.text.includes('Enable') || 
                                                                                   param.text.includes('Enabled') ||
                                                                                   displayValue === 'Enabled' ||
                                                                                   displayValue === 'Disabled' ||
                                                                                   displayValue === 'Yes' ||
                                                                                   displayValue === 'No';
                                                                    
                                                                    return isStatus ? getStatusBadge(displayValue) : <span>{displayValue}</span>;
                                                                })()}
                                                            </td>
                                                        ) : (
                                                            // Pass-specific or zone-specific values
                                                            passGroups.map((passKey) => {
                                                                const pass = headers[passKey];
                                                                const zones = Object.keys(pass.zones || {});
                                                                
                                                                // Check if this pass has a pass-specific value (merge zones)
                                                                const passValue = param.values.find((v: any) => v.groupName === passKey);
                                                                
                                                                if (passValue) {
                                                                    // Has pass-specific value - merge all zones in this pass
                                                                    // Try to get value from displayName first, then value property
                                                                    let displayValue = passValue?.displayName || passValue?.value;
                                                                    
                                                                    // Handle null, undefined, or empty string
                                                                    if (displayValue === undefined || displayValue === null || displayValue === '') {
                                                                        displayValue = '-';
                                                                    } else {
                                                                        displayValue = String(displayValue);
                                                                    }
                                                                    
                                                                    const isStatus = param.text.includes('Enable') || 
                                                                                   param.text.includes('Enabled') ||
                                                                                   displayValue === 'Enabled' ||
                                                                                   displayValue === 'Disabled' ||
                                                                                   displayValue === 'Yes' ||
                                                                                   displayValue === 'No';
                                                                    
                                                                    return (
                                                                        <td 
                                                                            key={passKey}
                                                                            colSpan={zones.length}
                                                                            className="px-2 py-2 text-sm text-center text-gray-700 bg-white font-mono border-b border-gray-200 align-middle"
                                                                        >
                                                                            {isStatus ? 
                                                                                getStatusBadge(displayValue) : 
                                                                                <span>{displayValue}</span>
                                                                            }
                                                                        </td>
                                                                    );
                                                                } else {
                                                                    // Zone-specific values - show individual cells
                                                                    return zones.map((zoneKey, zIdx) => {
                                                                        let valueForZone = param.values.find((v: any) => v.groupName === zoneKey);
                                                                        
                                                                        // Try to get value from displayName first, then value property
                                                                        let displayValue = valueForZone?.displayName || valueForZone?.value;
                                                                        
                                                                        // Handle null, undefined, or empty string
                                                                        if (displayValue === undefined || displayValue === null || displayValue === '') {
                                                                            displayValue = '-';
                                                                        } else {
                                                                            displayValue = String(displayValue);
                                                                        }
                                                                        
                                                                        const isStatus = param.text.includes('Enable') || 
                                                                                       param.text.includes('Enabled') ||
                                                                                       param.text.toLowerCase().includes('zone') ||
                                                                                       displayValue === 'Enabled' ||
                                                                                       displayValue === 'Disabled' ||
                                                                                       displayValue === 'Yes' ||
                                                                                       displayValue === 'No';
                                                                        
                                                                        const isLastZone = zIdx === zones.length - 1;
                                                                        
                                                                        return (
                                                                            <td 
                                                                                key={`${passKey}-${zoneKey}`}
                                                                                className={`min-w-[100px] px-2 py-2 text-sm text-center text-gray-700 bg-white font-mono border-b border-gray-200 align-middle ${isLastZone ? 'border-r border-gray-300' : ''}`}
                                                                            >
                                                                                {isStatus ? 
                                                                                    getStatusBadge(displayValue) : 
                                                                                    <span>{displayValue}</span>
                                                                                }
                                                                            </td>
                                                                        );
                                                                    });
                                                                }
                                                            })
                                                        )}
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
        </>
    );
};

export default PipeParametersTable;

